import { Component, ElementRef, inject, Inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CapitalizePipe } from '../../../core/pipes/capitalize.pipe';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HelpService } from '../../../core/services/help/help.service';

import { MatTooltipModule } from '@angular/material/tooltip';
import { SnackbarComponent } from '../snackbar/snackbar.component';
import { Platform } from '@angular/cdk/platform';
import { MediaQueryModule } from '../../directives/media-querys/media-query.module';

import { LocationStrategy } from '@angular/common';
import { PlatformServices } from '../../../core/services/platform/platform.service';
import { MaterialModule } from '../../modules/material/material.module';

@Component({
  selector: 'app-help-dialog',
  standalone: true,
  imports: [
    CommonModule, FontAwesomeModule, ReactiveFormsModule, TranslateModule,CapitalizePipe,
    MediaQueryModule, MaterialModule
  ],
  templateUrl: './help-dialog.component.html',
  styleUrl: './help-dialog.component.scss'
})
export class HelpDialogComponent {
  @ViewChild('inputFile' , {read: ElementRef, static: false} ) input!: ElementRef;
  @ViewChild('dialogContainer' , {read: ElementRef, static: false} ) dialogContainer!: ElementRef;

  step = 0;

  success = true;

  files: any[] = [];
  filesService: any[] = [];

  loadingFile: boolean = false;
  loadingService: boolean = false;

  formControl = new FormGroup({
    title: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required])
  });

  dialogRef!:MatDialogRef<HelpDialogComponent>;

  index = 10000;
  currentScreen: string = '';

  private noIndex = 10000;

  constructor(
    private helpService: HelpService,
    private dialog: MatDialog,
    private platform: PlatformServices,
    private location: LocationStrategy
  ) {
    this.currentScreen = this.platform.currentScreenSize;
    if (this.platform.currentScreenSize === 'Web') {
      this.dialogRef = inject(MatDialogRef);
    }
    gtag('event', 'page_view', {
      page_title: 'Help',
      page_path: ' ',
      page_location: 'help'
    })

  }

  onFileSelected(event: any) {
    this.loadingFile = true;
    const { files } = event.target as HTMLInputElement;

    if (files && files.length > 0) {
      const file = files[0];
      if( this.index != this.noIndex ) {
        this.filesService[this.index] = file;
      }else{
        this.filesService.push(file);
      }
      const selectedFile = event.target.files[0];
      this.convertToBase64(selectedFile);
    } else {
      this.loadingFile = false;
    }

    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
      this.scrollToBottom();
    }
  }

  openInputFile( input: any ){
    if( !this.loadingFile ){
      this.loadingFile = true;
      input.click();
    }
  }

  isValidImageType(fileType: string): boolean {
    return fileType.startsWith('data:image/');
  }

  async convertToBase64( file: any ) {
    if( file ){
      const reader = new FileReader();
      reader.onload = (e: any) => {
        if( this.index != this.noIndex ){
          this.files[this.index] = {
            name: file.name,
            base64: e.target.result,
            valid: file.size / (1024 * 1024) < 10,
          }
        }else{
          this.files.push( {
            name: file.name,
            base64: e.target.result,
            valid: file.size / (1024 * 1024) < 10,
          })

        }
        this.index = this.noIndex;
        this.loadingFile = false;
      };

      reader.readAsDataURL(file);
    }else{
      this.loadingFile = false;
    }

  }

  deleteFile( index: number ){
    this.files.splice(index, 1);
    this.filesService.splice(index, 1);
  }

  closeDialog(){ 
    this.helpService.hideHelp();
  }

  isFileValid(){
    return this.files.some( file => file.valid == false);
  }

  sendRequest(){
    this.loadingService = true;

    let request: any = this.formControl.value;

    if( this.filesService.length > 0 ){
      request.files = this.filesService;
    }else{
      delete request.files;
    }
    gtag('event', 'help_submit')

    this.helpService.sendHelp( request ).subscribe( {
      next: () => {
        if(this.platform.currentScreenSize === 'Web'){
          this.closeDialog();
          this.openSnackBar( 'success', 'help-modal.help.success-title', 'help-modal.help.message-success' );
        }
        this.success = true;
        this.step = 1;
        this.loadingService = false;
      },
      error: () => {
        if(this.platform.currentScreenSize === 'Web'){
          this.closeDialog();
          this.openSnackBar( 'error', '', 'help-modal.help.error-description' );
        }
        this.success = false;
        this.step = 1;
        this.loadingService = false;
      }
    })
  }

  openSnackBar( status: string, title: string, message: string ) {
    this.dialog.open( SnackbarComponent, {
      hasBackdrop: false,
      position:{
        top: "60px"
      },
      data:{
        status,
        message,
        title
      }
    })
  }

  retry(){
    this.step = 0;
    this.success = false;
  }

  replace(index: number){
    this.index = index;
    this.openInputFile(this.input.nativeElement)
  }

  scrollToBottom() {
    setTimeout(() => {
      this.dialogContainer.nativeElement.scrollTop = this.dialogContainer.nativeElement.scrollHeight;
    }, 100);
  }
}
